<template lang="pug">
.text-field-science
  .text-field-science__relative
    input.text-field-science__relative__input(
      type='text',
      v-model='content',
      ref='textfield',
      inputmode='none',
      readonly,
      @keydown.capture='onKeydown',
      @keyup='onKeyup',
      @compositionstart='onBlur',
      @compositionend='onFocus',
      @focus='onFocus',
      @click='onFocus',
      @blur='onBlur'
    )
  .text-field-science__background(:data-show='isShow')
    .text-field-science__background__handle(
      ref='handle',
      @mousedown='dragAttachmentStart',
      @mouseup='dragAttachmentEnd',
      @touchstart='touchAttachmentStart',
      @touchend='touchAttachmentEnd'
    )
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import { WebimeUtils } from '@/utils/WebimeUtils'

const HANDLE_HEIGHT = 60

/**
 * 化学式キーボード付き入力フィールド
 * フィールドクリックでキーボードが表示される
 */
@Component
export default class TextFieldScience extends Vue {
  private attachment: any = null
  private content = ''

  private isShow = false
  private shiftX = 0
  private shiftY = 0

  @Prop({ default: '' })
  value!: string

  @Prop({ default: false })
  readonly?: boolean

  @Ref() textfield!: HTMLInputElement

  @Ref() handle!: HTMLElement

  @Watch('attachment.state.isActive')
  private onChange() {
    this.isShow = this.attachment.state.isActive
    if (this.attachment.state.isActive) {
      // 移動用のハンドルブロックをアタッチメントの上部に表示
      const keyboardInner = document.getElementById('keyboard-container-inner')
      if (keyboardInner) {
        // ぴったり上部に表示するためにアタッチメントのradiusとmarginを0にする
        keyboardInner.style.borderRadius = '0'
        keyboardInner.style.margin = '0'
        const keyboardRect = keyboardInner.getBoundingClientRect()
        this.handle.style.width = `${keyboardRect.width}px`
        this.handle.style.left = `${keyboardRect.left}px`
        // ハンドルブロックの高さ分上に表示する
        this.handle.style.top = `${keyboardRect.top - HANDLE_HEIGHT}px`
      }
    } else {
      // アタッチメント位置をリセット
      const keyboard = document.getElementById('keyboard-container')
      if (keyboard) {
        keyboard.style.left = ''
        keyboard.style.top = ''
      }
    }
  }

  private beforeMount() {
    if (this.value) this.content = this.value
  }

  private mounted() {
    // カーソル移動時のイベントを登録 (documentにしか通知が来ないため、ここで対応)
    document.addEventListener('selectionchange', this.onSelectionChange)

    // アタッチメント (WebIMEとやり取りするためのモジュール)を設定
    this.attachment = Vue.prototype.$webime.createTextFieldAttachment({
      keyboardName: 'science',
      singleLine: true,
    })

    // IMEによる選択変更時
    this.attachment.on('selection-change', (evt: { start: number; end: number; preventDefault(e: boolean): void }) => {
      // 範囲外に出た場合、キャンセルを行う
      if (evt.start < 0 || this.content.length < evt.start || evt.end < 0 || this.content.length < evt.end) {
        evt.preventDefault(true)
        return
      }
      this.$nextTick(() => {
        // アクティブでない場合はカーソル移動しない
        const isActive = document.activeElement === this.textfield
        const willMove = evt.start !== this.textfield.selectionStart || evt.end !== this.textfield.selectionEnd

        // 移動が発生するときのみカーソル移動
        if (isActive && willMove) {
          this.textfield.setSelectionRange(evt.start, evt.end)
        }
      })
    })

    // IMEによるテキスト内容変更時
    this.attachment.on(
      'text-change',
      (evt: { start: number; end: number; text: string; preventDefault(e: boolean): void }) => {
        // readonlyの場合、キャンセルを行う
        if (this.readonly) {
          evt.preventDefault(true)
          return
        }

        // 範囲外に出た場合、キャンセルを行う
        if (evt.start < 0 || this.content.length < evt.start || evt.end < 0 || this.content.length < evt.end) {
          evt.preventDefault(true)
          return
        }

        // 範囲を書き換え
        const content = this.content ? this.content : ''
        const beforeString = content.slice(0, evt.start)
        const afterString = content.slice(evt.end)
        const newString = beforeString + evt.text + afterString

        // 新しい値を設定する
        this.content = newString
      }
    )
  }

  private beforeDestroy() {
    // カーソル移動時のイベントを削除 (documentにしか通知が来ないため、ここで対応)
    document.removeEventListener('selectionchange', this.onSelectionChange)

    // アタッチメントを解放
    Vue.prototype.$webime.releaseTextFieldAttachment(this.attachment)
    this.attachment = null
  }

  private onFocus() {
    // フォーカスを通知
    if (this.readonly) return
    this.attachment.focus()
    this.textfield.classList.add('use-webime')

    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
    const rect = this.textfield.getBoundingClientRect()
    window.scrollTo({
      top: rect.top + window.pageYOffset + rect.height / 2 - vh / 2,
      behavior: 'smooth',
    })
  }
  private onBlur() {
    // フォーカス解除を通知
    this.textfield.classList.remove('use-webime')
    this.attachment.blur()
  }
  private onKeydown(evt: Event) {
    // キーイベントを通知
    this.attachment.keydown(evt)
  }
  private onKeyup() {
    // キーイベントでカーソル移動した場合に選択範囲を更新
    if (this.attachment.state.isActive) {
      const start = this.textfield.selectionStart
      const end = this.textfield.selectionEnd
      this.attachment.setSelection(start, end, WebimeUtils.getAbsoluteBounds(this.textfield))
    }
  }
  private onSelectionChange() {
    // マウスでカーソル移動した場合に選択範囲を更新
    if (this.attachment.state.isActive) {
      const start = this.textfield.selectionStart
      const end = this.textfield.selectionEnd
      this.attachment.setSelection(start, end, WebimeUtils.getAbsoluteBounds(this.textfield))
    }
  }

  /**
   * マウスドラッグでアタッチメントを移動する
   */
  // マウスクリック時
  private dragAttachmentStart(e: MouseEvent) {
    // 他のマウスイベントをキャンセル
    e.preventDefault()
    // ハンドルブロック端からマウス位置への距離を取得
    const rect = this.handle.getBoundingClientRect()
    this.shiftX = e.clientX - rect.left
    this.shiftY = e.clientY - rect.top
    // マウス移動イベントを登録
    window.addEventListener('mousemove', this.moveAttachment)
  }
  // マウスを離した時
  private dragAttachmentEnd() {
    // マウス移動イベントを解除
    window.removeEventListener('mousemove', this.moveAttachment)
  }
  // マウス移動時のハンドルブロックとアタッチメント移動イベント
  private moveAttachment(e: MouseEvent) {
    // ハンドルブロックのleft/topにマウス位置から距離分を引いた値を設定する
    const left = e.clientX - this.shiftX
    const top = e.clientY - this.shiftY
    this.handle.style.left = left + 'px'
    this.handle.style.top = top + 'px'
    // アタッチメントはハンドルブロックの高さ分下の位置に設定する
    const keyboard = document.getElementById('keyboard-container')
    if (keyboard) {
      keyboard.style.left = left + 'px'
      keyboard.style.top = top + HANDLE_HEIGHT + 'px'
    }
  }

  /**
   * タッチイベントでアタッチメントを移動する
   */
  // 画面タッチ時
  private touchAttachmentStart(e: TouchEvent) {
    // 他のタッチイベントをキャンセル
    e.preventDefault()
    // ハンドルブロック端からタッチ位置への距離を取得
    const rect = this.handle.getBoundingClientRect()
    this.shiftX = e.touches[0].clientX - rect.left
    this.shiftY = e.touches[0].clientY - rect.top
    // タッチ移動イベントを登録
    window.addEventListener('touchmove', this.touchMoveAttachment)
  }
  // 画面タッチを離した時
  private touchAttachmentEnd() {
    // タッチ移動イベントを解除
    window.removeEventListener('touchmove', this.touchMoveAttachment)
  }
  // タッチ移動時のハンドルブロックとアタッチメント移動イベント
  private touchMoveAttachment(e: TouchEvent) {
    // ハンドルブロックのleft/topにタッチ位置から距離分を引いた値を設定する
    const left = e.touches[0].clientX - this.shiftX
    const top = e.touches[0].clientY - this.shiftY
    this.handle.style.left = left + 'px'
    this.handle.style.top = top + 'px'
    // アタッチメントはハンドルブロックの高さ分下の位置に設定する
    const keyboard = document.getElementById('keyboard-container')
    if (keyboard) {
      keyboard.style.left = left + 'px'
      keyboard.style.top = top + HANDLE_HEIGHT + 'px'
    }
  }

  @Watch('content')
  onContentChanged(newDate: string) {
    this.$emit('input', newDate)
  }
}
</script>

<style lang="stylus" scoped>
.text-field-science
  &__relative
    &__input
      width 12rem
      height 45px
      padding 5px
      border-radius 4px
      appearance none
      -webkit-appearance none
      -moz-appearance none

  &__background
    display none
    position fixed
    left 0
    top 0
    width 100%
    height 100%
    z-index 100
    &__handle
      display flex
      height 60px
      position absolute
      background var(--color-input-field-keyboard-handle__background)
      z-index 200
      border-radius 3px 3px 0 0
    &[data-show='true']
      display block
</style>
